import React, { useState } from 'react';
import { ChevronLeft, Camera, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Rating } from '../components/Rating';
interface EndRentalProps {
  tripId: string;
  onBack: () => void;
  onComplete: () => void;
  onSubmitClaim?: () => void;
}
export function EndRental({
  onBack,
  onComplete,
  onSubmitClaim
}: EndRentalProps) {
  const [photos, setPhotos] = useState<string[]>([]);
  const [condition, setCondition] = useState('');
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const maxPhotos = 5;
  const handleAddPhoto = () => {
    if (photos.length < maxPhotos) {
      const placeholders = ['https://images.unsplash.com/photo-1504148455328-c376907d081c?w=400&q=80', 'https://images.unsplash.com/photo-1572981779307-38b8cabb2407?w=400&q=80', 'https://images.unsplash.com/photo-1530124566582-a45a7e3e29f0?w=400&q=80', 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&q=80', 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=400&q=80'];
      const nextPhoto = placeholders[photos.length % placeholders.length];
      setPhotos([...photos, nextPhoto]);
    }
  };
  const handleRemovePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };
  const canSubmit = photos.length > 0;
  return <div className="min-h-screen bg-cream pb-24" data-id="element-1122">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-sand" data-id="element-1123">
        <div className="max-w-md mx-auto px-3 py-2 flex items-center" data-id="element-1124">
          <button onClick={onBack} className="p-1.5 -ml-1 hover:bg-sand/20 rounded-full transition-colors" data-id="element-1125">
            <ChevronLeft size={22} className="text-charcoal" data-id="element-1126" />
          </button>
          <h1 className="flex-1 text-base font-bold text-charcoal text-center pr-8" data-id="element-1127">
            End Booking
          </h1>
        </div>
      </div>

      <div className="max-w-md mx-auto" data-id="element-1128">
        {/* Instruction Text */}
        <div className="px-3 pt-3" data-id="element-1129">
          <p className="text-xs text-charcoal/80 leading-relaxed" data-id="element-1130">
            The owner has requested photos at the end of your booking. This
            helps protect both of you in case of damage or disputes.
          </p>
        </div>

        {/* Item Photos Section */}
        <div className="px-3 mt-3" data-id="element-1131">
          <label className="block text-xs font-bold text-charcoal mb-2" data-id="element-1132">
            Item Photos
          </label>

          <div className="flex flex-wrap gap-2" data-id="element-1133">
            <AnimatePresence data-id="element-1134">
              {photos.map((photo, index) => <motion.div key={`${photo}-${index}`} initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.8
            }} className="relative w-20 h-20 rounded-md overflow-hidden" data-id="element-1135">
                  <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" data-id="element-1136" />
                  <button onClick={() => handleRemovePhoto(index)} className="absolute top-1 right-1 p-0.5 bg-charcoal/70 rounded-full" data-id="element-1137">
                    <X size={12} className="text-white" data-id="element-1138" />
                  </button>
                </motion.div>)}
            </AnimatePresence>

            {photos.length < maxPhotos && <motion.button onClick={handleAddPhoto} whileTap={{
            scale: 0.95
          }} className="w-20 h-20 rounded-md border-2 border-dashed border-moss-gray flex flex-col items-center justify-center gap-1 text-moss-gray hover:border-dark-green hover:text-dark-green transition-colors" data-id="element-1139">
                <Camera size={24} data-id="element-1140" />
                <span className="text-[10px] font-medium" data-id="element-1141">Add Photo</span>
              </motion.button>}
          </div>

          <p className="text-[10px] text-moss-gray mt-1.5" data-id="element-1142">
            {photos.length} / {maxPhotos} photos
          </p>
        </div>

        {/* Condition Description Section */}
        <div className="px-3 mt-4" data-id="element-1143">
          <label className="block text-xs font-bold text-charcoal mb-2" data-id="element-1144">
            Describe the item's condition at drop-off
          </label>
          <textarea value={condition} onChange={e => setCondition(e.target.value)} placeholder="Include anything you notice about wear, damage, or functionality. Be as specific as possible." className="w-full h-[120px] p-2.5 border border-sand rounded text-xs text-charcoal placeholder-moss-gray leading-relaxed focus:outline-none focus:ring-1 focus:ring-dark-green focus:border-dark-green resize-y" data-id="element-1145" />
        </div>

        {/* Review Section (Optional) */}
        <div className="px-3 mt-3" data-id="element-1146">
          <label className="block text-xs font-bold text-charcoal mb-2" data-id="element-1147">
            Leave Review (optional)
          </label>

          {/* Star Rating */}
          <Rating value={rating} size={24} interactive onChange={setRating} data-id="element-1148" />

          {/* Review Question */}
          <p className="text-[11px] font-bold text-charcoal mt-2" data-id="element-1149">
            How was your experience borrowing from Oliver M.?
          </p>

          {/* Review Text Area */}
          <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="You can mention the item, communication, or how everything went overall." className="w-full h-[100px] mt-2 p-2.5 border border-sand rounded text-xs text-charcoal placeholder-moss-gray leading-relaxed focus:outline-none focus:ring-1 focus:ring-dark-green focus:border-dark-green resize-y" data-id="element-1150" />
        </div>

        {/* Action Buttons */}
        <div className="px-3 mt-3 mb-16 space-y-2" data-id="element-1151">
          <Button variant="primary" fullWidth disabled={!canSubmit} onClick={onComplete} data-id="element-1152">
            Complete Booking
          </Button>

          <Button variant="secondary" fullWidth onClick={onSubmitClaim} data-id="element-1153">
            Submit Claim
          </Button>
        </div>
      </div>
    </div>;
}